///
/// EarthBrowser
///
/// Created by Matt Giger on 9/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type CameraSavedState } from "@/types/CameraSavedState"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { validateSavedView } from "./validation/savedViewsValidation"

/**
 * A saved camera pose ("view"). Views are captured, never typed: the only
 * authoring gesture is "navigate somewhere, save". Tours (phase 5) reference
 * views by id and add choreography on top.
 */
export interface SavedView {
	/** uuid, generated client-side so create/update is one upsert verb */
	id: string
	name: string
	/** Camera pose, same shape the live camera persists (radius in km) */
	camera: CameraSavedState
	/** Optional entity to re-focus when flying here (a ?target= value) */
	focusTargetId?: string
	/**
	 * Dataset layers visible at save time. Applied enable-only on fly-to
	 * (never disables anything), so layer-dependent content like weather
	 * labels or satellites exists when the view is restored.
	 */
	layers?: string[]
	tags: string[]
	createdAt: string
	/** ISO date, drives sync conflict resolution (last-write-wins) */
	updatedAt: string
}

export interface SavedViewsState {
	views: Record<string, SavedView>
	/** Tombstones so deletions can propagate across devices (sync, phase 2) */
	deleted: Record<string, string>
	lastSyncedAt: string | null
}

const initialState: SavedViewsState = {
	views: {},
	deleted: {},
	lastSyncedAt: null,
}

export const savedViewsSlice = createSlice({
	name: "savedViews",
	initialState,
	reducers: {
		upsertView: (state, action: PayloadAction<SavedView>) => {
			const view = validateSavedView(action.payload)
			if (!view) return
			state.views[view.id] = view
			// Re-creating a previously deleted id clears its tombstone
			delete state.deleted[view.id]
		},
		renameView: {
			reducer: (state, action: PayloadAction<{ id: string; name: string; updatedAt: string }>) => {
				const { id, name, updatedAt } = action.payload
				const view = state.views[id]
				const trimmed = typeof name === "string" ? name.trim() : ""
				if (!view || trimmed.length === 0) return
				view.name = trimmed
				view.updatedAt = updatedAt
			},
			prepare: (payload: { id: string; name: string }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		setViewTags: {
			reducer: (state, action: PayloadAction<{ id: string; tags: string[]; updatedAt: string }>) => {
				const { id, tags, updatedAt } = action.payload
				const view = state.views[id]
				if (!view || !Array.isArray(tags)) return
				view.tags = tags.filter(tag => typeof tag === "string" && tag.length > 0)
				view.updatedAt = updatedAt
			},
			prepare: (payload: { id: string; tags: string[] }) => ({
				payload: { ...payload, updatedAt: new Date().toISOString() },
			}),
		},
		deleteView: {
			reducer: (state, action: PayloadAction<{ id: string; deletedAt: string }>) => {
				const { id, deletedAt } = action.payload
				if (!state.views[id]) return
				delete state.views[id]
				state.deleted[id] = deletedAt
			},
			prepare: (id: string) => ({
				payload: { id, deletedAt: new Date().toISOString() },
			}),
		},
		// Applied by launch validation to repair a corrupted persisted slice
		applyValidatedSavedViewsState: (_state, action: PayloadAction<SavedViewsState>) => {
			return action.payload
		},

		// ========== Sync (server merge) ==========

		/**
		 * Apply a server delta with per-view last-write-wins. Local edits
		 * newer than the server copy survive (the orchestrator pushes them
		 * up); server tombstones remove local views unless the local edit
		 * is newer than the deletion.
		 */
		mergeServerViews: (state, action: PayloadAction<{
			views: SavedView[]
			tombstones: Array<{ id: string; deletedAt: string }>
		}>) => {
			for (const incoming of action.payload.views) {
				const view = validateSavedView(incoming)
				if (!view) continue

				// A local deletion newer than the server copy wins (the
				// orchestrator pushes the delete up)
				const localDeletedAt = state.deleted[view.id]
				if (localDeletedAt && localDeletedAt >= view.updatedAt) continue

				const local = state.views[view.id]
				if (!local || view.updatedAt > local.updatedAt) {
					state.views[view.id] = view
					delete state.deleted[view.id]
				}
			}

			for (const tombstone of action.payload.tombstones) {
				const local = state.views[tombstone.id]
				// A local edit newer than the deletion wins (gets pushed up)
				if (local && local.updatedAt > tombstone.deletedAt) continue
				delete state.views[tombstone.id]
				// The server already owns this tombstone; no need to push it
				delete state.deleted[tombstone.id]
			}
		},

		/** Advance the sync cursor (only after all pushes succeeded) */
		markSynced: (state, action: PayloadAction<string>) => {
			state.lastSyncedAt = action.payload
		},

		/** Drop tombstones whose deletion reached the server */
		removeTombstones: (state, action: PayloadAction<string[]>) => {
			for (const id of action.payload) {
				delete state.deleted[id]
			}
		},
	},
})

export const {
	upsertView,
	renameView,
	setViewTags,
	deleteView,
	applyValidatedSavedViewsState,
	mergeServerViews,
	markSynced,
	removeTombstones,
} = savedViewsSlice.actions

export default savedViewsSlice.reducer
